import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Avatar, AvatarGroup, Box, Button, Card, CardContent, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublishIcon from '@mui/icons-material/Publish';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LockIcon from '@mui/icons-material/Lock';
import PlaceIcon from '@mui/icons-material/Place';
import { fileUrl } from '../../utils/fileUrl';
import { BANNER_SX } from './trainingRoom';
import {
    getMyEvents, getAllEvents, joinSession, cancelJoin, publishEvent, setEventStatus, deleteEvent,
    approveEvent, rejectEvent,
} from '../../api/eventApi';

const PP = "'Poppins', sans-serif";

const headSx = {
    fontFamily: PP, fontWeight: 700, fontSize: '0.74rem', color: '#475569',
    bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap',
};
const cellSx = { fontFamily: PP, fontSize: '0.82rem', color: '#1E293B' };

// Where the reader themselves stands on the next session. Separate from the event's
// own lifecycle colours above: one is about the event, this is about you.
const MY_STATUS_LABEL = {
    Joined: 'Registered',
    Attended: 'Attended',
    Requested: 'Requested',
    Waitlisted: 'Waitlisted',
};
const MY_STATUS_COLOR = {
    Joined: { bgcolor: '#DCFCE7', color: '#065F46' },
    Attended: { bgcolor: '#DBEAFE', color: '#1E40AF' },
    Requested: { bgcolor: '#FEF3C7', color: '#92400E' },
    Waitlisted: { bgcolor: '#F1F5F9', color: '#475569' },
};

// Keyed on the DISPLAY status - where the event is right now, not what somebody last
// pressed. 'On-going' is the loud one because it is the only one that is happening.
const STATUS_COLOR = {
    'On-going': ['#DCFCE7', '#065F46'],
    'Upcoming': ['#DBEAFE', '#1E40AF'],
    'Pending approval': ['#FEF3C7', '#92400E'],
    'Draft': ['#F1F5F9', '#475569'],
    'Cancelled': ['#FEE2E2', '#991B1B'],
    'Done': ['#EDE9FE', '#5B21B6'],
};

// What the reader is told, as against what the server calls it. "Done" is a task
// word; an event that has happened is Past, and one happening right now is Live.
// (user 2026-10-04: "for those pass event need to put Past")
const STATUS_LABEL = { 'Done': 'Past', 'On-going': 'Live now' };
const statusLabel = (s) => STATUS_LABEL[s] || s;

// What the reader is told, as against what is stored. "Targeted" is the value the API
// and the audience rules use and it stays that way; on screen it is Limited, which says
// what it means to somebody reading a list. The table used to print the raw value while
// the form called it "Hidden", so the two disagreed. (user 2026-10-04)
const VISIBILITY_LABEL = { Targeted: 'Limited' };
const visibilityLabel = (v) => VISIBILITY_LABEL[v] || v;

const TYPE_COLOR = { Training: '#0E8FD1', Travel: '#EA580C', Recruitment: '#7C3AED', Event: '#059669' };
const typeIcon = (t, sx) => t === 'Training' ? <SchoolIcon sx={sx} />
    : t === 'Travel' ? <FlightTakeoffIcon sx={sx} />
        : <EventIcon sx={sx} />;

const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: '2-digit' })
    : '—';
const fmtWhen = (iso) => iso
    ? new Date(iso).toLocaleString('en-MY', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
    : '';

/** How the recurrence reads in one phrase. */
function repeatsAs(e) {
    if (e.recurrenceType === 'Weekly') {
        const n = e.recurrenceInterval || 1;
        return n === 1 ? 'Weekly' : `Every ${n} weeks`;
    }
    if (e.recurrenceType === 'Monthly') {
        const weeks = (e.recurrenceWeeks || '').split(',').filter(Boolean);
        return weeks.length > 1 ? `${weeks.length}× a month` : 'Monthly';
    }
    return null;
}

/* ============================================================================
   EVENTS — one page, two views

   Everyone lands here. The table is the default because it is a working list you
   scan down; the card view is there for when you want to look at them rather than
   compare them, and the choice is remembered per browser.

   Create, edit and delete live in the table rather than behind their own menu
   entries - the list is where you already are when you decide to change
   something. (user 2026-10-03: "all event is table... card view / table view...
   able to create, edit, delete there in table. so remove the two menu")

   What a manager sees and what a participant sees come from the SAME endpoint
   pair: GetAll for managers (drafts included), GetMine for everyone else
   (audience-resolved, private events redacted to a title).
   ========================================================================= */
export default function EventsPage() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canManage, setCanManage] = useState(false);
    const [notice, setNotice] = useState('');
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [busyId, setBusyId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    /* CARDS ARE THE DEFAULT, on every screen. On a phone the table arrives with its
       columns off the right edge - date, venue and actions are not on screen until you
       drag sideways, and nothing says they are there. On a desktop the cards simply read
       better for a short list. Cards carry the same rows either way.
       (user 2026-10-26: "This can by default put cards? for event" / "1. for both")

       A SAVED CHOICE STILL WINS: this only decides what somebody sees before they have
       ever touched the toggle, so anyone who prefers the table keeps it. */
    const [view, setView] = useState(() => {
        try { return localStorage.getItem('EventsView') || 'card'; } catch { return 'card'; }
    });
    const setViewPref = (v) => {
        if (!v) return;
        setView(v);
        try { localStorage.setItem('EventsView', v); } catch { /* private mode */ }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Managers get the full list including drafts; it 403s for everyone else,
            // which is also how we learn which one this person is.
            try {
                const all = await getAllEvents();
                setEvents(all.data || []);
                setCanManage(true);
            } catch {
                const mine = await getMyEvents();
                setEvents(mine.data || []);
                setCanManage(false);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const now = Date.now();

    // ONE list, not two tabs. Anything still to come sits at the top, soonest first,
    // because that is what somebody opening this page is looking for; everything that
    // has been and gone falls below it, most recent first. (user 2026-10-03: "old one
    // can remain downwards, new one put on top")
    const ordered = useMemo(() => {
        const endOf = e => e.nextSession?.endsOn ? new Date(e.nextSession.endsOn).getTime() : null;
        const startOf = e => new Date(e.nextSession?.startsOn || e.createdOn || 0).getTime();
        const up = [], past = [];
        events.forEach(e => {
            const end = endOf(e);
            (end == null || end >= now ? up : past).push(e);
        });
        up.sort((a, b) => startOf(a) - startOf(b));
        past.sort((a, b) => startOf(b) - startOf(a));
        return { rows: [...up, ...past], upcomingCount: up.length, pastCount: past.length };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);

    const shown = useMemo(
        () => ordered.rows.slice(page * perPage, page * perPage + perPage),
        [ordered, page, perPage],
    );
    // The first row on this page that has already happened - the divider hangs off it.
    const firstPastId = useMemo(() => {
        const first = shown.find(e => {
            const end = e.nextSession?.endsOn ? new Date(e.nextSession.endsOn).getTime() : null;
            return end != null && end < now;
        });
        return first?.eventId ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shown]);

    // ── actions ─────────────────────────────────────────────────────────────
    const cancel = async (ev) => {
        const sid = ev.nextSession?.eventSessionId;
        if (!sid) return;
        setBusyId(ev.eventId);
        try {
            const res = await cancelJoin(sid);
            setNotice(res.data?.message || 'Your place has been released.');
            await load();
        } catch (err) {
            setNotice(err?.response?.data?.message || 'Could not release that place.');
        } finally {
            setBusyId(null);
        }
    };

    const register = async (ev) => {
        const sid = ev.nextSession?.eventSessionId;
        if (!sid) return;
        setBusyId(ev.eventId);
        try {
            const res = await joinSession(sid);
            setNotice(res.data?.message || 'Registered.');
            await load();
        } catch (err) {
            setNotice(err?.response?.data?.message || 'Could not register.');
        } finally {
            setBusyId(null);
        }
    };

    const doPublish = async (e) => {
        try {
            const res = await publishEvent(e.eventId);
            setNotice(res.data?.message || 'Published.');
            await load();
        } catch (err) { setNotice(err?.response?.data?.message || 'Could not publish.'); }
    };

    const doCancel = async (e) => {
        try {
            await setEventStatus(e.eventId, 'Cancelled');
            setNotice(`"${e.title}" cancelled.`);
            await load();
        } catch (err) { setNotice(err?.response?.data?.message || 'Could not cancel.'); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            const res = await deleteEvent(confirmDelete.eventId);
            setNotice(res.data?.message || 'Deleted.');
            setConfirmDelete(null);
            await load();
        } catch (err) { setNotice(err?.response?.data?.message || 'Could not delete.'); }
    };

    const doApprove = async (e) => {
        try {
            const res = await approveEvent(e.eventId);
            setNotice(res.data?.message || 'Released.');
            await load();
        } catch (err) { setNotice(err?.response?.data?.message || 'Could not approve.'); }
    };

    const doReject = async (e) => {
        try {
            const res = await rejectEvent(e.eventId, '');
            setNotice(res.data?.message || 'Sent back.');
            await load();
        } catch (err) { setNotice(err?.response?.data?.message || 'Could not send it back.'); }
    };

    const Actions = ({ e }) => (
        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
            <Tooltip title="Edit">
                <IconButton size="small" onClick={() => navigate(`/events/${e.eventId}/edit`)}>
                    <EditIcon sx={{ fontSize: 17, color: '#475569' }} />
                </IconButton>
            </Tooltip>
            {e.status === 'PendingApproval' ? (
                <>
                    <Tooltip title="Approve and release">
                        <IconButton size="small" onClick={() => doApprove(e)}>
                            <CheckCircleIcon sx={{ fontSize: 17, color: '#059669' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Send back as a draft">
                        <IconButton size="small" onClick={() => doReject(e)}>
                            <UndoIcon sx={{ fontSize: 17, color: '#92400E' }} />
                        </IconButton>
                    </Tooltip>
                </>
            ) : e.status !== 'Published' ? (
                <Tooltip title="Publish">
                    <IconButton size="small" onClick={() => doPublish(e)}>
                        <PublishIcon sx={{ fontSize: 17, color: '#0E8FD1' }} />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Cancel this event">
                    <IconButton size="small" onClick={() => doCancel(e)}>
                        <BlockIcon sx={{ fontSize: 17, color: '#92400E' }} />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setConfirmDelete(e)}>
                    <DeleteOutlineIcon sx={{ fontSize: 17, color: '#DC2626' }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );

    // ── the two views ───────────────────────────────────────────────────────
    const TableView = () => (
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}>Event</TableCell>
                            <TableCell sx={headSx}>Type</TableCell>
                            {/* An attendee does not care that a series repeats weekly - they
                                care whether this one is on now, coming, or over. The organiser
                                sets the recurrence, so they keep it. (user 2026-10-04: "i
                                think donnid write repeat or weekly")
                                It is headed STATUS, the same word the organiser's view uses for
                                the same chip - two names for one thing was the confusing part.
                                (user 2026-10-04: "when isit status? please align same as super
                                admin title for column") */}
                            <TableCell sx={headSx}>{canManage ? 'Repeats' : 'Status'}</TableCell>
                            {canManage && <TableCell sx={headSx}>Status</TableCell>}
                            {canManage && <TableCell sx={headSx}>Visibility</TableCell>}
                            {canManage && <TableCell sx={headSx} align="right">Reaches</TableCell>}
                            <TableCell sx={headSx}>Next</TableCell>
                            <TableCell sx={headSx} align="right">Going</TableCell>
                            {/* An attendee reads two different things here - where they stand,
                                and what they can do about it - so they are two columns with
                                names on them rather than one unlabelled cell that changed
                                shape depending on the answer. (user 2026-10-04) */}
                            {/* And the reader's own standing, which is a different question
                                from the event's - so a different name. */}
                            {!canManage && <TableCell sx={headSx}>My status</TableCell>}
                            <TableCell sx={headSx} align="right">{canManage ? 'Actions' : 'Action'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shown.length === 0 && (
                            <TableRow><TableCell colSpan={9} align="center" sx={{ ...cellSx, py: 5, color: '#94A3B8' }}>
                                No events yet
                            </TableCell></TableRow>
                        )}
                        {shown.map(e => {
                            const divider = e.eventId === firstPastId;
                            const [bg, fg] = STATUS_COLOR[e.displayStatus] || STATUS_COLOR.Draft;
                            const s = e.nextSession;
                            const booked = s?.myStatus && !['Cancelled', 'Declined'].includes(s.myStatus);
                            const rep = repeatsAs(e);
                            return (
                              <>
                                {divider && (
                                    <TableRow key={`past-${e.eventId}`}>
                                        <TableCell colSpan={9} sx={{
                                            py: 0.5, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0',
                                            fontFamily: PP, fontSize: '0.68rem', fontWeight: 700,
                                            color: '#94A3B8', letterSpacing: '.5px',
                                        }}>
                                            ALREADY HAPPENED
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow key={e.eventId} hover
                                    sx={{ cursor: e.isTeaser ? 'default' : 'pointer' }}
                                    onClick={() => { if (!e.isTeaser) navigate(`/events/${e.eventId}`); }}>
                                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            {typeIcon(e.type, { fontSize: 16, color: TYPE_COLOR[e.type] || '#059669' })}
                                            <span>{e.title || '(untitled)'}</span>
                                            {e.isTeaser && <LockIcon sx={{ fontSize: 14, color: '#94A3B8' }} />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={cellSx}>{e.type}</TableCell>
                                    <TableCell sx={cellSx}>
                                        {canManage ? (
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B' }}>
                                                {rep || 'One time'}
                                            </Typography>
                                        ) : (
                                            <Chip label={statusLabel(e.displayStatus || e.status)} size="small"
                                                sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: bg, color: fg }} />
                                        )}
                                    </TableCell>
                                    {canManage && (
                                        <TableCell>
                                            <Chip label={statusLabel(e.displayStatus || e.status)} size="small"
                                                sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: bg, color: fg }} />
                                        </TableCell>
                                    )}
                                    {canManage && <TableCell sx={{ ...cellSx, color: '#64748B' }}>{visibilityLabel(e.visibility)}</TableCell>}
                                    {canManage && <TableCell sx={cellSx} align="right">{e.audienceCount ?? '—'}</TableCell>}
                                    {/* The date is a way IN to the full session list, not just a
                                        label. A series can hold dozens of sessions and this column
                                        shows one, which reads as though one is all there is — the
                                        rest are a page deeper, under Sessions & Attendance on the
                                        edit screen, and nothing pointed there.
                                        Only for people who can edit: it is their screen.
                                        (user 2026-10-19: "how come i cant see back previous or
                                        upcoming one?") */}
                                    <TableCell sx={{ ...cellSx, color: '#64748B' }}>
                                        {canManage && s?.startsOn ? (
                                            <Box
                                                component="span"
                                                onClick={evt => { evt.stopPropagation(); navigate(`/events/${e.eventId}/edit#sessions`); }}
                                                title="See every session for this series"
                                                sx={{
                                                    cursor: 'pointer', color: '#1A8CFF', fontWeight: 600,
                                                    textDecoration: 'underline', textUnderlineOffset: '2px',
                                                    '&:hover': { color: '#0B6FCC' },
                                                }}
                                            >
                                                {fmtDate(s.startsOn)}
                                            </Box>
                                        ) : fmtDate(s?.startsOn)}
                                    </TableCell>
                                    <TableCell sx={cellSx} align="right">{s?.joinedCount ?? 0}</TableCell>
                                    {!canManage && (
                                        <TableCell sx={cellSx}>
                                            {booked ? (
                                                <Chip label={MY_STATUS_LABEL[s.myStatus] || s.myStatus} size="small"
                                                    sx={{
                                                        height: 20, fontSize: '0.62rem', fontWeight: 700,
                                                        ...(MY_STATUS_COLOR[s.myStatus] || MY_STATUS_COLOR.Joined),
                                                    }} />
                                            ) : (
                                                <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#94A3B8' }}>
                                                    Not booked
                                                </Typography>
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell align="right" onClick={evt => evt.stopPropagation()}>
                                        {canManage ? <Actions e={e} />
                                            : e.isTeaser || !s ? null
                                            : booked ? (
                                                // Cancelling is back on the row, but as the ACTION
                                                // column rather than a loose button beside the badge
                                                // - which is what made it clutter the first time.
                                                // (user 2026-10-03 removed it; 2026-10-04 asked for
                                                // status + action instead)
                                                <Button size="small" variant="outlined" disabled={busyId === e.eventId}
                                                    onClick={() => cancel(e)}
                                                    sx={{
                                                        fontFamily: PP, textTransform: 'none', fontSize: '0.72rem',
                                                        color: '#B45309', borderColor: '#FCD9A5',
                                                        '&:hover': { borderColor: '#B45309', bgcolor: '#FFF7ED' },
                                                    }}>
                                                    Cancel
                                                </Button>
                                            ) : (
                                                <Button size="small" variant="contained" disabled={busyId === e.eventId}
                                                    onClick={() => register(e)}
                                                    sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.72rem' }}>
                                                    Register
                                                </Button>
                                            )}
                                    </TableCell>
                                </TableRow>
                              </>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={ordered.rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={perPage}
                onRowsPerPageChange={e => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                    borderTop: '1px solid #E2E8F0',
                    // The two labels are <p> elements and pick up a page-wide paragraph
                    // margin, which drops them off the line the select and the arrows sit
                    // on. Styling every descendant with '& *' changed the font but left
                    // that margin, so the row read crooked. Same shape as payoutReport.
                    '& .MuiTablePagination-toolbar': {
                        fontFamily: PP, fontSize: '0.78rem', minHeight: 44, alignItems: 'center',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        margin: 0, fontFamily: PP, fontSize: '0.78rem', lineHeight: 1.6,
                    },
                    '& .MuiTablePagination-select': { fontFamily: PP, fontSize: '0.78rem' },
                    '& .MuiTablePagination-actions button': { padding: '6px' },
                }}
            />
        </Paper>
    );

    const CardView = () => (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2 }}>
            {shown.length === 0 && (
                <Card sx={{ borderRadius: '12px', gridColumn: '1 / -1' }}>
                    <CardContent sx={{ textAlign: 'center', py: 5 }}>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#64748B' }}>
                            No events yet
                        </Typography>
                    </CardContent>
                </Card>
            )}
            {shown.map(e => {
                const s = e.nextSession;
                const booked = s?.myStatus && !['Cancelled', 'Declined'].includes(s.myStatus);
                const rep = repeatsAs(e);
                return (
                    <Card key={e.eventId}
                        onClick={() => { if (!e.isTeaser) navigate(`/events/${e.eventId}`); }}
                        sx={{
                            borderRadius: '14px', overflow: 'hidden',
                            borderLeft: `4px solid ${e.isTeaser ? '#CBD5E1' : (TYPE_COLOR[e.type] || '#059669')}`,
                            cursor: e.isTeaser ? 'default' : 'pointer',
                            opacity: e.isTeaser ? 0.75 : 1,
                            '&:hover': { boxShadow: e.isTeaser ? undefined : '0 4px 16px rgba(15,23,42,.1)' },
                        }}>
                        {e.bannerUrl && !e.isTeaser && (
<Box component="img" src={fileUrl(e.bannerUrl)} alt=""
                                sx={{ ...BANNER_SX, borderRadius: 0 }} />
                        )}
                        <CardContent sx={{ p: '14px !important' }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                {typeIcon(e.type, { fontSize: 17, color: TYPE_COLOR[e.type] || '#059669' })}
                                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.92rem', color: '#0F172A' }}>
                                    {e.title}
                                </Typography>
                                {e.isTeaser && (
                                    <Chip icon={<LockIcon sx={{ fontSize: 13 }} />} label="By invitation" size="small"
                                        sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                                )}
                                {/* On a card the state matters more than the cadence, same as the
                                    table. The organiser still sees the cadence. */}
                                {!e.isTeaser && (
                                    <Chip label={statusLabel(e.displayStatus || e.status)} size="small"
                                        sx={{
                                            height: 20, fontSize: '0.62rem', fontWeight: 700,
                                            bgcolor: (STATUS_COLOR[e.displayStatus] || STATUS_COLOR.Draft)[0],
                                            color: (STATUS_COLOR[e.displayStatus] || STATUS_COLOR.Draft)[1],
                                        }} />
                                )}
                                {canManage && rep && !e.isTeaser && (
                                    <Chip label={rep} size="small"
                                        sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#EFF6FF', color: '#0E8FD1' }} />
                                )}
                                {booked && (
                                    <Chip label="Registered" size="small"
                                        sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#DCFCE7', color: '#065F46' }} />
                                )}
                            </Stack>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: '#475569', fontWeight: 600 }}>
                                {fmtWhen(s?.startsOn)}
                            </Typography>
                            {e.isTeaser ? (
                                <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#94A3B8', mt: 0.75 }}>
                                    By invitation. Only the invitees see the details.
                                </Typography>
                            ) : (
                                <>
                                    {e.description && (
                                        <Typography sx={{
                                            fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mt: 0.5,
                                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        }}>{e.description}</Typography>
                                    )}
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
                                        {!e.isOnline && e.venue && (
                                            <Stack direction="row" spacing={0.4} alignItems="center">
                                                <PlaceIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                                                <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B' }}>{e.venue}</Typography>
                                            </Stack>
                                        )}
                                        {(s?.attendees || []).length > 0 && (
                                            <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: '0.6rem', fontFamily: PP, borderWidth: 1.5 } }}>
                                                {s.attendees.map(a => (
                                                    <Tooltip key={a.userId} title={a.name}>
                                                        <Avatar src={fileUrl(a.profilePhotoUrl)}>{(a.name || '?').charAt(0)}</Avatar>
                                                    </Tooltip>
                                                ))}
                                            </AvatarGroup>
                                        )}
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B' }}>
                                            {s?.joinedCount ?? 0} going
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Box onClick={evt => evt.stopPropagation()}>
                                            {canManage ? <Actions e={e} />
                                                : (s && !booked && (
                                                    <Button size="small" variant="contained" disabled={busyId === e.eventId}
                                                        onClick={() => register(e)}
                                                        sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.74rem' }}>
                                                        Register
                                                    </Button>
                                                ))}
                                        </Box>
                                    </Stack>
                                </>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            <Stack direction="row" alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.2 }}>
                        Events
                    </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => setViewPref(v)}>
                    <ToggleButton value="table" sx={{ textTransform: 'none', fontFamily: PP }}>
                        <TableRowsIcon sx={{ fontSize: 17, mr: 0.5 }} /> Table
                    </ToggleButton>
                    <ToggleButton value="card" sx={{ textTransform: 'none', fontFamily: PP }}>
                        <ViewModuleIcon sx={{ fontSize: 17, mr: 0.5 }} /> Cards
                    </ToggleButton>
                </ToggleButtonGroup>
                {canManage && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/events/new')}
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
                        Create Event
                    </Button>
                )}
            </Stack>

            {notice && (
                <Box sx={{ bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', p: 1.25, mb: 2 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#1E40AF' }}>{notice}</Typography>
                </Box>
            )}

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={26} /></Stack>
            ) : view === 'table' ? <TableView /> : <CardView />}

            {/* ── delete confirmation ─────────────────────────────────────── */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>Delete this event?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#1E293B' }}>
                        <b>{confirmDelete?.title}</b>
                    </Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#64748B', mt: 1 }}>
                        This removes {(confirmDelete?.sessions || []).length} session(s), everyone's bookings and
                        the whole chat. It cannot be undone.
                    </Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#92400E', mt: 1 }}>
                        If it is simply not happening, cancelling the event keeps the record instead.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)} sx={{ fontFamily: PP, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={doDelete}
                        sx={{ fontFamily: PP, textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
